import type { IFilter, ISectionedFilters } from "types/filters";

import { List, Map } from "immutable";
import { UserEntitlementModel } from "models/UserEntitlementModel";
import { apiReqAbortOld } from "utils/api/apiReqAbortOld";
import { paginatedSearchOptionsToBody } from "utils/api/urlSearchParams";
import { type IPaginationResponse, withPagination } from "utils/pagination";
import { type IPaginatedSearchOptions } from "utils/searchUtils";

import { type TAdminUser, type TUserRole, UserModel } from "../models/UserModel";
import { apiReq } from "../utils/api/apiReq";

export async function getPaginatedUsers(
  paginationOptions?: IPaginatedSearchOptions,
  {
    abortKey = "getPaginatedUsers",
    filters,
    includeDeleted = true,
    withExtendedData = false,
    withFgaScoping,
  }: { abortKey?: string; filters?: IFilter[] | ISectionedFilters; includeDeleted?: boolean; withExtendedData?: boolean; withFgaScoping?: boolean } = {},
): Promise<IPaginationResponse<UserModel>> {
  const body: Record<string, unknown> = {
    withDeleted: includeDeleted,
    withExtendedData,
    ...(withFgaScoping !== undefined && { withFgaScoping }),
  };

  Object.assign(body, paginatedSearchOptionsToBody(paginationOptions, filters));

  return withPagination(
    (user: Record<string, unknown>) => UserModel.fromServerData(user),
    () => apiReqAbortOld({ body, key: abortKey, method: "POST", path: "/v1/users/search" }),
  );
}

export async function multiGetUsers(
  ids: string[],
  options: { includeDeleted?: boolean } = {},
): Promise<List<UserModel>> {
  if (!ids.length) return List<UserModel>();
  const { data } = await apiReq(
    "GET",
    `/v1/users/multiGet?withDeleted=${!!options.includeDeleted}&${ids.map((id) => "ids=" + id).join("&")}`,
  );

  return List<UserModel>(data.map((item: unknown) => UserModel.fromServerData(item)));
}

const isAdminUser = (user: UserModel): user is TAdminUser => user.isAdmin;

export async function changeUsersRole(userId: string, role: TUserRole): Promise<UserModel> {
  const { data } = await apiReq("PUT", "/v1/users/" + userId, { role });

  return UserModel.fromServerData(data);
}

export async function getAdmins(): Promise<Map<string, TAdminUser>> {
  const { data } = await apiReq<unknown[]>("GET", "/v1/users/admins");
  return Map(
    data.map((user: unknown) => {
      const model = UserModel.fromServerData(user);
      if (!isAdminUser(model)) throw new Error("Expected admin user");
      return [model.id, model];
    }),
  );
}

export async function getManagedUserIds(): Promise<string[]> {
  const { data } = await apiReq<string[]>("GET", "/v1/users/managedUsers");
  return data;
}

export async function getUser(id: string): Promise<UserModel> {
  const { data } = await apiReq("GET", `/v1/users/${id}`);

  return UserModel.fromServerData(data);
}

export async function getUserEntitlements(id: string, withUnmanaged = false): Promise<List<UserEntitlementModel>> {
  const { data } = await apiReq("GET", `/v1/users/${id}/entitlements?withUnmanaged=${withUnmanaged}`);
  return List(data.map((permission: unknown) => UserEntitlementModel.fromServerData(permission)));
}
